import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface AnchoredModalProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  placement?: Placement;
  offset?: number;
  children: React.ReactNode;
  panelClassName?: string;
  zIndexClassName?: string;
}

export const AnchoredModal: React.FC<AnchoredModalProps> = ({
  open,
  anchorRef,
  onClose,
  placement = 'bottom-start',
  offset = 8,
  children,
  panelClassName,
  zIndexClassName = 'z-50',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  const updatePosition = () => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const ar = anchor.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;

    let top = 0;
    let left = 0;

    const fitsBelow = ar.bottom + offset + ph <= window.innerHeight;
    const fitsAbove = ar.top - offset - ph >= 0;

    const finalPlacement =
      placement.startsWith('bottom') && !fitsBelow && fitsAbove
        ? (placement.replace('bottom', 'top') as Placement)
        : placement;

    if (finalPlacement.startsWith('bottom')) {
      top = ar.bottom + offset;
    } else {
      top = ar.top - ph - offset;
    }

    if (finalPlacement.endsWith('start')) {
      left = ar.left;
    } else {
      left = ar.right - pw;
    }

    const pad = 8;
    left = Math.max(pad, Math.min(left, window.innerWidth - pw - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - ph - pad));

    setStyle({ top, left, visibility: 'visible' });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement, offset]);

  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (!panel || !anchor) return;
      if (!panel.contains(e.target as Node) && !anchor.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleScrollOrResize = () => updatePosition();

    window.addEventListener('keydown', handleKey);
    window.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className={`fixed inset-0 ${zIndexClassName} pointer-events-none`}>
      <div
        ref={panelRef}
        className={`absolute pointer-events-auto ${panelClassName ?? ''}`}
        style={style}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default AnchoredModal;
